import {
  AnnotationBase,
  ElementStatus,
  MoveEvent,
  PageStructure,
  TextRect,
  ToolMessage,
  useMove,
  usePointerMove
} from '@st/document-viewer-kit'
import {
  Anchor,
  atAnchor,
  Point,
  Rect,
  rectContainsPoint,
  rectFromPoints,
  rectIntersects
} from '@st/geom'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { clsx } from 'clsx'
import { useMemo, useState } from 'react'
import { SelectToolState } from '~/st-doc-viewer/annotations'

export const INITIAL_TOOL_STATE: SelectToolState = {
  name: 'select',
  settings: {}
}

const SELECT_TOOL_DEBUG_MODE = false

type SelectTextState =
  /**
   * Nothing is being done. Mouse is up and no text has been selected.
   */
  | { status: 'idle' }

  /**s
   * The mouse is currently pressed down and the use is dragging to select text.
   * Once they release the mouse, the plugin will transition to the `selected` state
   * if they have selected any text.
   *
   * If nothing has been selected, the plugin will transition back to the `idle` state.
   */
  | { status: 'selecting'; start: Point; end: Point; selections: Selection[] }
  /**
   * The use has released the mouse and some text has been selected.
   *
   * In this state you will see selection rectangles as you see in many document viewers
   * when you have text selected.
   *
   * Hitting CMD+C should copy taht selected text to the clipboard.
   */
  | { status: 'selected'; selections: Selection[]; selectedText: string }

/**
 * An invisible overlay that responds to mouse drag events and allows the user to the underlying
 * text of the document.
 *
 * This component has nothing to do with rendering text.
 *
 * The only visual output is the blue selection rectangles that appears when text is selected.
 */
export function SelectToolOverlay({
  page
}: {
  page: PageStructure
  toolState: SelectToolState
  activeAnnotation: AnnotationBase | undefined
  status: ElementStatus
  send: (message: ToolMessage<AnnotationBase, SelectToolState>) => void
}) {
  const [state, setState] = useState<SelectTextState>({ status: 'idle' })

  // We can lean on the useMove hook to handle the click and drag to select mouse interaction
  //
  // The sequence is as follows:
  // 1. The user clicks down on the mouse. and the 'start' event is triggered.
  // 2. The user drags the mouse. and the 'move' event is continuously triggered as the mouse moves with the mouse down.
  //    It is during this time when the blue selection rectangles are updated as the mouse moves.
  // 3. The user releases the mouse. and the 'end' event is triggered.
  //    If the user has selected any text, the plugin will transition to the `selected` state and the
  //    selected blue rectangles stay persistent.
  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove: (e) => {
      // A deterministic state machine that handles the logic of what happens
      // Everything about the next state can be computed from the current event and the
      // page.textRects
      const nextState = nextSelectionState(state, page, e)
      if (nextState !== state) {
        setState(nextState)
      }
    }
  })

  // If text is selected and the user presses CMD+C
  // we copy the selected text to the clipboard
  useKeyboardShortcuts({
    '$mod+c': () => {
      if (state.status == 'selected') {
        navigator.clipboard.writeText(state.selectedText)
      }
    }
  })

  const selections: TextSelectionPair[] | undefined = useMemo(() => {
    if (state.status == 'idle') return undefined
    return state.selections
      .map((selection) => {
        const textRect = page.textRects[selection.index]
        return { textRect, selection }
      })
      .filter((p) => p.selection != undefined)
  }, [
    page.textRects,
    state.status == 'selecting',
    state.status == 'selecting' ? state.selections : undefined
  ])

  const selectionHandleRect =
    state.status === 'selecting' ? rectFromPoints(state.start, state.end) : null

  return (
    <div
      ref={ref}
      className={clsx(
        'absolute bottom-0 left-0 right-0 top-0 bg-transparent',
        state.status == 'selecting' && 'cursor-text'
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {selectionHandleRect && (
        <div
          style={{
            position: 'absolute',
            top: selectionHandleRect.y,
            left: selectionHandleRect.x,
            width: selectionHandleRect.width,
            height: selectionHandleRect.height
          }}
        />
      )}
      {SELECT_TOOL_DEBUG_MODE && state.status === 'selected' && (
        <SelectedTextPreviewPanel text={state.selectedText} />
      )}
      {SELECT_TOOL_DEBUG_MODE && <TextBoundingBoxesDebugLayer page={page} />}
      <OverTextCursorOverlay page={page} />
      {selections && <SelectedTextHighlightsLayer selectionPairs={selections} />}
    </div>
  )
}

/**
 * An invisible overlay that detects when the mouse is over text.
 */
function OverTextCursorOverlay({ page }: { page: PageStructure }) {
  const [isOverText, setIsOverText] = useState(false)

  const { ref, onPointerMove } = usePointerMove({
    canvasClass: 'drawing-canvas',
    onPointerMove: (e) => {
      const selection = page.textRects.some((textRect) =>
        rectContainsPoint(textRect.bounds, e.point)
      )
      setIsOverText(selection)
    }
  })
  return (
    <div
      className={clsx(
        'absolute bottom-0 left-0 right-0 top-0 bg-transparent',
        isOverText ? 'cursor-text' : 'cursor-default'
      )}
      ref={ref}
      onPointerMove={onPointerMove}
    />
  )
}

type TextSelectionPair = {
  textRect: TextRect
  selection: Selection
}

function SelectedTextHighlightsLayer({ selectionPairs }: { selectionPairs: TextSelectionPair[] }) {
  return (
    <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-0">
      {selectionPairs.map(({ textRect, selection }) => {
        return (
          <div
            key={selection.index}
            style={{
              position: 'absolute',
              top: textRect.bounds.y,
              left: textRect.bounds.x,
              width: textRect.bounds.width,
              height: textRect.bounds.height
            }}
          >
            {textRect.charBounds.map((charBounds, charIndex) => {
              const isInSelection =
                selection && selection.range[0] <= charIndex && charIndex < selection.range[1]

              return (
                <div
                  key={charIndex}
                  className={clsx({
                    'bg-blue-600/30': isInSelection
                  })}
                  style={{
                    position: 'absolute',
                    top: charBounds.y - textRect.bounds.y,
                    left: charBounds.x - textRect.bounds.x,
                    width: charBounds.width,
                    height: charBounds.height
                  }}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

function TextBoundingBoxesDebugLayer({ page }: { page: PageStructure }) {
  return (
    <div className="absolute bottom-0 left-0 right-0 top-0">
      {page.textRects.map((textRect, rectIndex) => {
        return (
          <div
            key={rectIndex}
            style={{
              position: 'absolute',
              top: textRect.bounds.y,
              left: textRect.bounds.x,
              width: textRect.bounds.width,
              height: textRect.bounds.height
              // outline: selection ? '1px solid red' : '1px solid lightblue'
            }}
          >
            {textRect.charBounds.map((charBounds, charIndex) => {
              return (
                <div
                  key={charIndex}
                  style={{
                    position: 'absolute',
                    top: charBounds.y - textRect.bounds.y,
                    left: charBounds.x - textRect.bounds.x,
                    width: charBounds.width,
                    height: charBounds.height,
                    // backgroundColor: isInSelection ? 'rgba(255, 255, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'
                    border: '0.1px solid grey'
                  }}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

function nextSelectionState(
  state: SelectTextState,
  page: PageStructure,
  e: MoveEvent
): SelectTextState {
  switch (e.type) {
    case 'start':
      // we start off with a rectangle at the mosue down point with width and height 0
      return {
        status: 'selecting',
        start: e.start.point,
        end: e.start.point,
        selections: []
      }
    case 'move':
      if (state.status != 'selecting') return state
      // We update the selection bounds of the rectangle as the mouse moves
      return {
        status: 'selecting',
        start: state.start,
        end: e.current.point,
        selections: calculateSelections(page.textRects, state.start, e.current.point)
      }
    case 'end':
      if (state.status != 'selecting') return state

      // The mouse was just released so we calculate what text was selected
      const selections = calculateSelections(page.textRects, state.start, e.current.point)

      // If anything was selected, we go into the selected state
      // Otherwise we go back to the idle state waiting for input
      if (selections.length > 0) {
        return {
          status: 'selected',
          selections,
          selectedText: highlightedTextsToString(
            selections.map((s): HighlightedText => {
              const textRect = page.textRects[s.index]
              return {
                contents: textRect.contents.slice(s.range[0], s.range[1]),
                bounds: textRect.bounds
              }
            })
          )
        }
      } else {
        return { status: 'idle' }
      }
  }
}

/**
 * A reference to the selected text.
 */
type Selection = {
  /**
   * The index of the text rectangle that contains the selected text.
   */
  index: number

  /**
   * The start and end indices of the selected text within the text rectangle.
   * Calling `text.slice(range[0], range[1])` will return the selected text.
   */
  range: [number, number]
}

/**
 * This is the meat of the text selection algorithm.
 * It calculates, given the mouse drag start and end points,
 * which text rectangles are selected and what character ranges within each are selected.
 *
 * It is applied in realtime (pixel by pixel) as the user drags the mouse to select text.
 */
export function calculateSelections(
  textRects: TextRect[],
  dragStart: Point,
  dragEnd: Point
): Selection[] {
  const selections: Selection[] = []

  // Create a bounding box for the entire selection area
  const selectDragBounds = rectFromPoints(dragStart, dragEnd)
  const topLeft = atAnchor(selectDragBounds, Anchor.topLeft)
  const bottomRight = atAnchor(selectDragBounds, Anchor.bottomRight)

  for (let index = 0; index < textRects.length; index++) {
    const textRect = textRects[index]

    // Skip text rectangles that don't intersect with the selection area at all
    if (!rectIntersects(textRect.bounds, selectDragBounds)) {
      continue
    }

    // Check if the selection starts or ends within this text rectangle
    const selectionStartsInside = rectContainsPoint(textRect.bounds, topLeft)
    const selectionEndsInside = rectContainsPoint(textRect.bounds, bottomRight)

    // Find the indices of the first and last characters whose bounds intersect the selection rectangle
    // Note: findIndex/findLastIndex return -1 if no element satisfies the condition.
    const firstIntersectingCharIndex = textRect.charBounds.findIndex((cb) =>
      rectIntersects(cb, selectDragBounds)
    )
    const lastIntersectingCharIndex = textRect.charBounds.findLastIndex((cb) =>
      rectIntersects(cb, selectDragBounds)
    )

    // Determine the start of the character range for this text rectangle:
    // - If selectionStartsInside and an intersecting char was found, start at that char's index.
    // - Otherwise (selection starts before this rect, or no specific char intersects), start at index 0.
    const rangeStart =
      selectionStartsInside && firstIntersectingCharIndex !== -1 ? firstIntersectingCharIndex : 0

    // Determine the end of the character range for this text rectangle:
    // - If selectionEndsInside and an intersecting char was found, end *after* that char's index.
    // - Otherwise (selection ends after this rect, or no specific char intersects), end at the total length.
    const rangeEnd =
      selectionEndsInside && lastIntersectingCharIndex !== -1
        ? lastIntersectingCharIndex + 1
        : textRect.charBounds.length

    // Add the calculated selection range for this text rectangle
    // Note: The original logic implicitly handled cases where findIndex/findLastIndex returned -1
    // by defaulting to [0, length]. This refactored logic achieves the same result.
    // A basic sanity check (rangeStart <= rangeEnd) might be added if needed, but
    // the current logic should prevent start > end.
    selections.push({
      index,
      range: [rangeStart, rangeEnd]
    })
  }

  return selections
}

function SelectedTextPreviewPanel({ text }: { text: string }) {
  return (
    <div className="absolute right-4 top-4 z-50 whitespace-pre bg-stone-800 p-2 text-xs text-white">
      {text}
    </div>
  )
}

type HighlightedText = { contents: string; bounds: Rect }

/**
 * Given a list of text rectangles that were selected with the mouse,
 * return a single concatenated string that represents the text that was selected.
 *
 * This must account for fuzzy nature of the problem.
 *
 * For example:
 * - Text rectangles don't always line up exactly
 * - Documents (PDFs in particular) do not have layout information that tells us
 *   what is a paragraph, word, sentence, etc.
 *
 * To account for this, we assume that if two pieces of text have a vertical overlap,
 * of 60% of more, they are on the same line.
 */
function highlightedTextsToString(selections: HighlightedText[]): string {
  if (selections.length == 0) return ''

  // Sort once: top‑to‑bottom, then left‑to‑right
  const ordered = [...selections].sort((a, b) =>
    a.bounds.y === b.bounds.y ? a.bounds.x - b.bounds.x : a.bounds.y - b.bounds.y
  )

  const SAME_LINE_THRESHOLD = 0.6

  function overlapRatio(aBounds: Rect, bBounds: Rect): number {
    const top = Math.max(aBounds.y, bBounds.y)
    const bottom = Math.min(aBounds.y + aBounds.height, bBounds.y + bBounds.height)
    const overlap = Math.max(0, bottom - top)
    const shorter = Math.min(aBounds.height, bBounds.height)
    return shorter == 0 ? 0 : overlap / shorter
  }

  let result = ordered[0].contents.trim()
  let prevBounds = ordered[0].bounds

  for (let i = 1; i < ordered.length; i++) {
    const curr = ordered[i]
    const sameLine = overlapRatio(prevBounds, curr.bounds) >= SAME_LINE_THRESHOLD

    if (sameLine) {
      // Ensure a single space between words on the same visual line.
      if (!/\s$/.test(result)) result += ' '
      result += curr.contents.trimStart()
    } else {
      // Different visual line → newline.
      result += '\n' + curr.contents.trimStart()
    }

    prevBounds = curr.bounds
  }

  return result
}
